import { RoomEntityRaw, RoomIsLive, RoomIsRecording } from "@common/domain";
import { Action, createReducer, on } from "@ngrx/store";
import { BrandActions } from "@youm-front/brand/data-access";
import { LicenseActions } from "@youm-front/license/data-access";
import { RoomActions, RoomRecordingActions } from "../actions";

export const ROOM_FEATURE_KEY = "room";

export interface RoomState {
  room: null | RoomEntityRaw;
  isLoading: boolean;
  joinInProgress: boolean;
  joinSucceed: boolean;
  joinErrored: null | Error;
}

const initialState: RoomState = {
  room: null,
  isLoading: false,
  joinInProgress: false,
  joinSucceed: false,
  joinErrored: null
};

const reducer = createReducer(
  initialState,
  on(
    RoomActions.init,
    RoomActions.destroy,
    RoomActions.reset,
    () => initialState
  ),
  on(RoomActions.joinRoom, (state) => ({
    ...state,
    joinInProgress: true,
    joinSucceed: false,
    joinErrored: null
  })),
  on(RoomActions.joinRoomFailure, (state, action) => ({
    ...state,
    joinInProgress: false,
    joinSucceed: false,
    joinErrored: action.error
  })),
  on(RoomActions.joinRoomSuccess, (state) => ({
    ...state,
    joinInProgress: false,
    joinSucceed: true,
    joinErrored: null
  })),
  on(
    RoomActions.loadRoomData,
    RoomActions.startLiveStream,
    RoomActions.stopLiveStream,
    (state) => ({
      ...state,
      isLoading: true
    })
  ),
  on(
    RoomActions.loadRoomDataFailure,
    RoomActions.startLiveStreamFailure,
    RoomActions.stopLiveStreamFailure,
    (state) => ({
      ...state,
      isLoading: false
    })
  ),
  on(
    RoomActions.loadRoomDataSuccess,
    RoomActions.startLiveStreamSuccess,
    (state, action) => ({
      ...state,
      room: action.room.toJSON(),
      isLoading: false
    })
  ),
  on(RoomActions.stopLiveStreamSuccess, (state, action) => {
    return {
      ...state,
      room:
        state.room?.id === action.roomId
          ? {
              ...state.room,
              isLive: false as RoomIsLive
            }
          : state.room,
      isLoading: false
    };
  }),
  on(LicenseActions.licenseLimitMaxLiveDuration, (state, action) => {
    if (!state.room) {
      return state;
    }

    if (state.room.id !== action.licenseLimitExceededVO.value.roomId) {
      return state;
    }

    return {
      ...state,
      room: {
        ...state.room,
        isLive: false as RoomIsLive
      }
    };
  }),
  on(BrandActions.brandCreateSuccess, (state, action) => {
    if (!state.room) {
      return state;
    }

    return {
      ...state,
      room: {
        ...state.room,
        brandId: action.brand.value.id
      }
    };
  }),
  on(BrandActions.brandInUse, (state, action) => {
    if (!state.room) {
      return state;
    }

    return {
      ...state,
      room: {
        ...state.room,
        brandId: action.brandId
      }
    };
  }),
  on(RoomRecordingActions.roomRecordingDelete, (state, action) => {
    if (action.roomRecordingDeleteCmd.value.roomId !== state.room?.id) {
      return state;
    }

    return {
      ...state,
      isLoading: true
    };
  }),
  on(RoomRecordingActions.roomRecordingDeleteSuccess, (state, action) => {
    const { roomId } = action.roomRecordingDeleteCmd.value;

    if (roomId !== state.room?.id) {
      return state;
    }

    return {
      ...state,
      room: {
        ...state.room,
        isRecording: false as RoomIsRecording
      },
      isLoading: false
    };
  }),
  on(RoomRecordingActions.roomRecordingDeleteFailure, (state, action) => {
    const { roomId, roomIsRecording } = action.roomRecordingDeleteCmd.value;

    if (roomId !== state.room?.id) {
      return state;
    }

    return {
      ...state,
      room: {
        ...state.room,
        isRecording: roomIsRecording
      },
      isLoading: false
    };
  }),
  on(RoomRecordingActions.roomRecordingSet, (state, action) => {
    const { roomId } = action.roomRecordingSetCmd.value;

    if (roomId !== state.room?.id) {
      return state;
    }

    return {
      ...state,
      isLoading: true
    };
  }),
  on(RoomRecordingActions.roomRecordingSetSuccess, (state, action) => {
    const { roomId } = action.roomRecordingSetCmd.value;

    if (roomId !== state.room?.id) {
      return state;
    }

    return {
      ...state,
      room: {
        ...state.room,
        isRecording: true as RoomIsRecording
      },
      isLoading: false
    };
  }),
  on(RoomRecordingActions.roomRecordingSetFailure, (state, action) => {
    const { roomId, roomIsRecording } = action.roomRecordingSetCmd.value;

    if (roomId !== state.room?.id) {
      return state;
    }

    return {
      ...state,
      room: {
        ...state.room,
        isRecording: roomIsRecording
      },
      isLoading: false
    };
  })
);

export function roomReducer(state: RoomState | undefined, action: Action) {
  return reducer(state, action);
}
